import { axiosInstance } from "@helpers/axios-instance";
import type { DataAwareResponse, FinancialAccount, Household } from "../types/types";
import { useQueryWithValidation } from "./useQueryWithValidation";

export type LTIPDataAwareResponse = {
  primaryClientHasData: boolean;
  secondaryClientHasData: boolean;
  bothClientsHaveData?: boolean;
} & DataAwareResponse<FinancialAccount[]>;

export function useLTIP(
  householdExternalCode: string,
  type: FinancialAccount["accountType"],
  selectedClient?: string | null | undefined,
  household?: Partial<Household>
) {
  const typeToPathTranslation: Record<
    Exclude<FinancialAccount["accountType"], undefined>,
    string
  > = {
    SARs: "stock-appreciation-rights",
    RSU: "restricted-stock-awards",
    PSU: "performance-stock-awards",
    CDP: "compensation-deferral-plan",
    DCP: "deferred-compensation-plan",
    SRP: "savings-restoration-plan",
    DLTIP: "deferred-long-term-incentive-plan",
  };

  const typeToUse = typeToPathTranslation[type as keyof typeof typeToPathTranslation] || type;

  return useQueryWithValidation({
    queryKey: [String(typeToUse), householdExternalCode, String(selectedClient)],
    queryFn: async () => {
      const data = await axiosInstance({
        method: "GET",
        url:  `Salesforce/LTIP/${type}/${householdExternalCode}`,
      });

      const fas = data.data.data as FinancialAccount[];
      let primaryClientHasData = false;
      let secondaryClientHasData = false;
      let bothClientsHaveData = false;

      if (!household?.isSingle) {
        const primaryClientData = fas?.find(
          (d) => d.ownerId === household?.primaryContact?.externalCode
        );
        const secondaryClientData = fas?.find(
          (d) => d.ownerId === household?.secondaryContact?.externalCode
        );

        primaryClientHasData = !!primaryClientData;
        secondaryClientHasData = !!secondaryClientData;
        bothClientsHaveData = primaryClientHasData && secondaryClientHasData;
      }

      const newWrapper = { ...data.data };
      newWrapper.primaryClientHasData = primaryClientHasData;
      newWrapper.secondaryClientHasData = secondaryClientHasData;
      newWrapper.bothClientsHaveData = bothClientsHaveData;

      if (selectedClient) {
        const newData = fas?.filter((d) => d.ownerId === selectedClient);

        newWrapper.data = newData;

        return newWrapper as LTIPDataAwareResponse;
      }

      return newWrapper as LTIPDataAwareResponse;
    },
    enabled: !!householdExternalCode,
  });
}
